import {
	Card,
	Divider,
	Pagination,
	Tag,
	message,
	Button,
	// Modal, Input, Select
} from 'antd';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { DownloadOutlined } from '@ant-design/icons';
import NativeNumber from '../../component/Numeral';
import baseuri from '../../methods/baseuri';
import { commas } from '../../methods/utils/general-utils';

// import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';

import SearchFilter from '../../component/Filter';
import remakeQueryString from '../../methods/utils/remake-query-string';
import {
	// createProxyInvestment,
	investmentStatusCount,
	investmentTopUpList,
	investmentTopUpStatusCount,
	investmentWalletBalance,
	retrieveAdminInvestmentItems,
	// retrieveInvestmentItems,
	unregisteredInvestmentItems,
	// startProxyInvestment,
} from '../../methods/redux/reducers/investment';
import InvestmentList from './InvestmentLists';
import { getCurrencies } from '../../methods/redux/reducers/currency';
import InvestmentStatusCount from './InvestmentStatusCounts';
import { retrieveInternalSettings } from '../../methods/redux/reducers/internal-settings';

const Style = styled.div`
	& .mini-report {
		& > * {
			display: inline-block;
			margin-right: 10px;
			margin-bottom: 10px;
		}
		& .amount {
			font-weight: 600;
			font-size: 16px;

			&.red {
				color: var(--red-color);
			}
		}
	}

	& .out-links {
		display: inline-block;
		margin-right: 20px;
		text-decoration: underline;
	}
`;

const DownloadButton = styled(Button)`
	color: red;
	background-color: #f5d5d5;
	border-radius: 5px;
	&:hover {
		color: red;
		background-color: #efe7e7;
	}
	&:focus {
		color: red;
		background-color: #f5d5d5;
	}
`;

const InvestmentUsers = () => {
	const dispatch = useAppDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const {
		investmentItems,
		investmentStatus,
		unregisteredInvestments,
		investmentBal,
		investmentTopUpStatus,
		adminInvestmentItems,
	} = useAppSelector((store) => store.investment);
	const settings = useAppSelector((store) => store.internalSettings);

	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState([] as any);
	const getQueryParams = () => Object.fromEntries(searchParams.entries());
	const csvLinkRef = useRef<
		CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
	>(null);

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length === 0) {
			setSearchParams({
				pageId: '1',
				perPage: String(investmentItems.paginator?.perPage),
			});
		}
		dispatch(retrieveAdminInvestmentItems({ ...queryData }));
		// dispatch(retrieveInvestmentItems({ ...queryData }));
		dispatch(investmentStatusCount({}));
		dispatch(getCurrencies({}));
		dispatch(investmentWalletBalance({}));
		dispatch(retrieveInternalSettings());
	}, []);

	const updateQueryString = (query: any, action = 'update') => {
		remakeQueryString(query, searchParams, setSearchParams, action);
	};
	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			dispatch(retrieveAdminInvestmentItems({ ...queryData }));
			// dispatch(retrieveInvestmentItems({ ...queryData }));
			dispatch(investmentStatusCount({ ...queryData }));
			dispatch(unregisteredInvestmentItems({}));
			dispatch(investmentTopUpList({ ...queryData }));
			dispatch(investmentTopUpStatusCount({ ...queryData }));
		}
	}, [searchParams]);

	const investmentFormSelect = [
		{
			name: 'REAL_ESTATE',
			value: 'REAL_ESTATE',
		},
		{
			name: 'STOCKS',
			value: 'STOCKS',
		},
	];
	const isRegisteredSelect = [
		{
			name: 'Registered',
			value: true,
		},
		{
			name: 'Unregistered',
			value: false,
		},
	];

	// Transform the results data to match the headers
	const transformDownloadRequests = (requests: any[]) =>
		requests?.map((data: any) => ({
			title: data?.title,
			id: data?._id,
			username: data?.userId?.username,
			investmentForm: data.investmentForm,
			riseInvestmentId: data?.riseInvestmentId,
			investmentStatus: data.investmentStatus,
			status: data.status,
			deposit: `${commas(data?.totalDeposit || 0)} ${data?.currencyId?.symbol}`,
			profit: `${commas(data?.totalProfit || 0)} ${data?.currencyId?.symbol}`,
			withdrawn: `${commas(data?.totalWithdrawn || 0)} ${
				data?.currencyId?.symbol
			}`,
			createdAt: moment(data.createdAt).format('lll'), // Handle formatted date
		}));

	// Define headers with simple keys
	const headers = [
		{ label: 'Title', key: 'title' },
		{ label: 'Id', key: 'id' },
		{ label: 'Username', key: 'username' },
		{ label: 'Investment Form', key: 'investmentForm' },
		{ label: 'Rise Investment ID', key: 'riseInvestmentId' },
		{ label: 'Investment Status', key: 'investmentStatus' },
		{ label: 'Status', key: 'status' },
		{ label: 'Total Deposit', key: 'deposit' },
		{ label: 'Total Profit', key: 'profit' },
		{ label: 'Total Withdrawn', key: 'withdrawn' },
		{ label: 'Created At', key: 'createdAt' },
	];

	const handleDownload = () => {
		setLoading(true);
		const params1 = getQueryParams();

		setResults([]);
		baseuri
			.get('/admin/investments' || '', {
				params: {
					...params1,
					perPage: adminInvestmentItems?.paginator?.itemCount || 0,
					pageId: '1',
				},
			})
			.then((res: any) => {
				const rawData = res.data.data.itemsList;
				const transformedData = transformDownloadRequests(rawData);
				setResults(transformedData);
				setLoading(false);
			})
			.catch(() => {
				message.error('could not download CSV');
				setLoading(false);
			});
	};

	useEffect(() => {
		if (results.length > 0) {
			csvLinkRef?.current?.link.click();
		}
	}, [results]);

	return (
		<Style>
			<Card>
				<h1>Investment Profile </h1>
				<p>
					This page displays a paginated and comprehensive list of all
					investments{' '}
				</p>
				<h2 style={{ marginTop: '25px', color: 'green' }}>
					<strong style={{ color: 'black' }}>Investment Wallet Balance:</strong>{' '}
					<NativeNumber value={investmentBal.balance} decimalPlaces={4} />{' '}
					{investmentBal.currency}
				</h2>
				<strong>
					Min Investment Amount: {settings.minInvestmentAmountInUSD} USD
				</strong>
				<br />
				<strong>
					Risevest Interest Percentage : {settings.risevestInterestPercentage} %
				</strong>
				<p>
					<span style={{ fontStyle: 'italic', fontSize: '12px' }}>
						Note: The risevest interest percentage shows how much percent of the
						risevest Gain to be payed to users
					</span>
				</p>

				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
					}}
				>
					{/* <div style={{ marginRight: '10px' }}>
						<Button
							disabled={isStartInvestment}
							type="primary"
							onClick={() => setVisible(true)}
						>
							Start Investment
						</Button>
					</div> */}

					{/* <div>
						<Button
							disabled={!isStartInvestment}
							type="primary"
							onClick={() => setVisibleCreateInvestment(true)}
						>
							Create Investment
						</Button>
					</div> */}
				</div>

				<Divider />
				<strong>Investment Counts:</strong>
				<div className="mini-report">
					<Tag
						color="black"
						style={{
							padding: '7px 15px',
							marginRight: '10px',
							marginBottom: '10px',
						}}
					>
						<p>Total</p>
						<p>
							<NativeNumber
								value={adminInvestmentItems?.paginator?.itemCount}
							/>
						</p>
					</Tag>

					<Tag
						color="red"
						style={{
							padding: '7px 15px',
							marginRight: '10px',
							marginBottom: '10px',
						}}
					>
						<Link
							style={{ color: 'red' }}
							title="Investment Topups"
							className="out-links"
							to="/core-modules/investment/unregistered-users"
						>
							<p style={{ color: 'red' }}>Unregistered Users</p>
							<p style={{ color: 'red' }}>
								<NativeNumber
									value={unregisteredInvestments?.paginator?.itemCount}
								/>
							</p>
						</Link>
					</Tag>

					<InvestmentStatusCount statusCount={investmentStatus} />
				</div>

				<strong>TopUp Counts:</strong>

				<div className="mini-report">
					<Tag
						color="black"
						style={{
							padding: '7px 15px',
							marginRight: '10px',
							marginBottom: '10px',
						}}
					>
						<p style={{ color: 'white' }}>Total</p>
						<p style={{ color: 'white' }}>
							<NativeNumber value={investmentTopUpStatus.all} />
						</p>
					</Tag>

					<Tag
						color="red"
						style={{
							padding: '7px 15px',
							marginRight: '10px',
							marginBottom: '10px',
						}}
					>
						<Link
							style={{ color: 'red' }}
							title="Investment Topups"
							className="out-links"
							to="/core-modules/investment/top-ups?isMigrated=false"
						>
							<p>Unmigrated TopUp</p>
							<p>
								<NativeNumber value={investmentTopUpStatus.pendingMigrate} />
							</p>
						</Link>
					</Tag>
					<Tag
						color="blue"
						style={{
							padding: '7px 15px',
							marginRight: '10px',
							marginBottom: '10px',
						}}
					>
						<p>Migrated TopUp</p>
						<p>
							<NativeNumber value={investmentTopUpStatus.migrated} />
						</p>
					</Tag>
				</div>
				<br />
				<div>
					<Link
						title="Investment Topups"
						className="out-links"
						to="/core-modules/investment/top-ups"
					>
						Go To Investment Top-up Items
					</Link>
					{/* <Link
						style={{
							color:
								unregisteredInvestments?.paginator?.itemCount >= 1
									? 'red'
									: 'black',
						}}
						title="Investment Topups"
						className="out-links"
						to="/core-modules/investment/unregistered-users"
					>
						Go To Unregistered Investment
					</Link> */}
					<Link
						title="Settings"
						className="out-links"
						to="/settings/investment-settings"
					>
						Settings
					</Link>
					<Link
						title="Investment Topups"
						className="out-links"
						to="/core-modules/investment/withdrawals"
					>
						Withdrawals
					</Link>
				</div>
				<br />
				<Divider />
				<SearchFilter
					title="Search Investment"
					searchFilters={[
						{
							label: 'investmentStatus',
							name: 'investmentStatus',
							defaultSelectedValue: 'running',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'running', value: 'running' },
								{ name: 'Completed', value: 'completed' },
								{ name: 'terminated', value: 'terminated' },
								{ name: 'liquidated', value: 'liquidated' },
							],
						},
						{
							label: 'investmentForm',
							name: 'investmentForm',
							htmlElement: 'select',
							selectOptions: investmentFormSelect,
						},
						{
							label: 'isRegistered',
							name: 'isRegistered',
							htmlElement: 'select',
							selectOptions: isRegisteredSelect,
						},
						{
							label: 'status',
							name: 'status',
							defaultSelectedValue: 'active',
							htmlElement: 'select',
							selectOptions: [
								{ name: 'active', value: 'active' },
								{ name: 'pending', value: 'pending' },
								{ name: 'deleted', value: 'deleted' },
							],
						},
					]}
					retrieveQueryString={updateQueryString}
				/>

				<br />

				<p>
					Total of{' '}
					<strong>{adminInvestmentItems?.paginator?.itemCount} items</strong>{' '}
					found
				</p>
				<Divider />

				<div>
					<Pagination
						onChange={(pageId) => updateQueryString({ pageId })}
						total={adminInvestmentItems?.paginator?.itemCount}
						current={adminInvestmentItems?.paginator?.currentPage}
						pageSize={adminInvestmentItems?.paginator?.perPage ?? 20}
					/>
				</div>

				{adminInvestmentItems && adminInvestmentItems.itemsList?.length > 0 && (
					<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
						<DownloadButton
							danger
							loading={loading}
							icon={<DownloadOutlined />}
							onClick={() => handleDownload()}
						>
							{loading ? 'Loading csv...' : 'Download CSV'}
						</DownloadButton>
						<CSVLink
							data={results}
							headers={headers}
							className="download"
							filename="InvestmentTopup.csv"
							ref={csvLinkRef}
						/>
					</div>
				)}

				<br />

				{adminInvestmentItems &&
					adminInvestmentItems?.itemsList?.map((index: any) => (
						<InvestmentList key={index._id} investmentProfile={index} />
					))}

				<br />

				<Pagination
					onChange={(pageId) => updateQueryString({ pageId })}
					total={adminInvestmentItems?.paginator?.itemCount}
					current={adminInvestmentItems?.paginator?.currentPage}
					pageSize={adminInvestmentItems?.paginator?.perPage ?? 20}
					showSizeChanger
					showQuickJumper
				/>
			</Card>
			{/* <Modal
				okText="Start Investment"
				title="Start Investment"
				onOk={submit}
				visible={visible}
				onCancel={() => setVisible(false)}
			>
				<p>Bvn Number</p>
				<Input
					value={state.bvn}
					onChange={(e) => setState({ ...state, bvn: e.target.value })}
				/>
			</Modal>

			<Modal
				okText="Create Investment"
				title="Create Investment"
				onOk={submitCreateInvestment}
				visible={visibleCreateInvestment}
				onCancel={() => setVisibleCreateInvestment(false)}
			>
				<p>Title</p>
				<Input
					value={createState.title}
					onChange={(e) =>
						setCreateState({ ...createState, title: e.target.value })
					}
				/>
				<p>Amount</p>
				<Input
					value={createState.amount}
					onChange={(e) =>
						setCreateState({ ...createState, amount: Number(e.target.value) })
					}
				/>
				<p>Investment Form</p>
				<Select
					defaultValue={createState.investmentForm}
					style={{ width: '100%' }}
					onChange={(value) =>
						setCreateState({ ...createState, investmentForm: value })
					}
				>
					{investForm?.map((i: any) => (
						<Select.Option key={i.key} value={i.key}>
							{i.label}
						</Select.Option>
					))}
				</Select>

				<p>Duration In Month</p>
				<Select
					defaultValue={createState.durationInMonth}
					style={{ width: '100%' }}
					onChange={(value) =>
						setCreateState({ ...createState, durationInMonth: value })
					}
				>
					{duration?.map((i: any) => (
						<Select.Option key={i.key} value={i.key}>
							{i.label}
						</Select.Option>
					))}
				</Select>

				<p>ReInvest After Maturity</p>
				<Select
					defaultValue={createState.reInvestAfterMaturity}
					style={{ width: '100%' }}
					onChange={(value) =>
						setCreateState({
							...createState,
							reInvestAfterMaturity: Boolean(value),
						})
					}
				>
					{reInvest?.map((i: any) => (
						<Select.Option key={i.key} value={i.key}>
							{i.label}
						</Select.Option>
					))}
				</Select>

				<p>Currency</p>
				<Select
					defaultValue={createState.currencyId}
					style={{ width: '100%' }}
					onChange={(value) =>
						setCreateState({ ...createState, currencyId: value })
					}
				>
					{currencies
						?.filter((i: any) => i.useInvestment)
						?.map((i: any) => (
							<Select.Option key={i._id} value={i._id}>
								{i.symbol}
							</Select.Option>
						))}
				</Select>
			</Modal> */}
		</Style>
	);
};

export default InvestmentUsers;
